import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { subscriptionAPI, paymentAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../../components/ui/accordion';
import {
  Crown,
  Check,
  X,
  Zap,
  Shield,
  Clock,
  Star,
  Sparkles,
  ShoppingCart,
  CheckSquare,
  Users,
  BarChart3,
  Bell,
  FileText,
  CalendarDays,
  Download,
  HeadphonesIcon,
  Receipt,
  Globe,
  Package,
  Wallet,
  TrendingUp,
  Lock,
  AlertTriangle,
  ArrowRight,
  Infinity,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const PERIODS = [
  { months: 1, label: '1 месяц' },
  { months: 3, label: '3 месяца' },
  { months: 6, label: '6 месяцев' },
  { months: 12, label: '12 месяцев' },
];

function PaymentHistorySection() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadPayments = async () => {
    if (loaded) return;
    setLoading(true);
    try {
      const res = await paymentAPI.getHistory();
      setPayments(res.data.payments || []);
      setLoaded(true);
    } catch (e) {
      toast.error('Не удалось загрузить историю платежей');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadPayments();
  };

  const statusMap = {
    confirmed: { label: 'Оплачен', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
    pending: { label: 'Ожидание', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
    rejected: { label: 'Отклонён', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' },
    refunded: { label: 'Возврат', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20' },
    expired: { label: 'Истёк', color: 'bg-gray-500/10 text-muted-foreground border-gray-500/20' },
  };

  const periodLabel = (m) => m === 1 ? '1 мес.' : m === 3 ? '3 мес.' : m === 6 ? '6 мес.' : '12 мес.';

  return (
    <div className="max-w-4xl mx-auto">
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-between px-5 py-3.5 rounded-xl border border-border bg-card hover:bg-accent/50 transition-colors group"
      >
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
            <Receipt className="h-4.5 w-4.5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <span className="font-medium text-sm">История платежей</span>
          {loaded && payments.length > 0 && (
            <Badge variant="secondary" className="text-xs">{payments.length}</Badge>
          )}
        </div>
        <ArrowRight className={cn(
          "h-4 w-4 text-muted-foreground transition-transform duration-200",
          open && "rotate-90"
        )} />
      </button>

      {open && (
        <div className="mt-3 rounded-xl border border-border bg-card overflow-hidden animate-in slide-in-from-top-2 duration-200">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-12 px-4">
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                <Receipt className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-muted-foreground">Платежей пока нет</p>
              <p className="text-xs text-muted-foreground/60 mt-1">Здесь будет история ваших оплат</p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden sm:block">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-5 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Дата</th>
                      <th className="text-left px-5 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Период</th>
                      <th className="text-right px-5 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Сумма</th>
                      <th className="text-right px-5 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p, i) => {
                      const s = statusMap[p.status] || statusMap.pending;
                      return (
                        <tr key={p.id || i} className={cn("border-b border-border/50 last:border-0 hover:bg-muted/20 transition-colors", i % 2 === 1 && "bg-muted/10")}>
                          <td className="px-5 py-3.5">
                            <div className="font-medium">
                              {new Date(p.created_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {new Date(p.created_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <Badge variant="outline" className="font-normal">{periodLabel(p.period_months)}</Badge>
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            <span className="font-semibold">{p.amount_rub?.toLocaleString('ru-RU')} ₽</span>
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            <span className={cn("inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border", s.color)}>
                              {s.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="sm:hidden divide-y divide-border">
                {payments.map((p, i) => {
                  const s = statusMap[p.status] || statusMap.pending;
                  return (
                    <div key={p.id || i} className="px-4 py-3.5 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium">
                          {new Date(p.created_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-muted-foreground">{periodLabel(p.period_months)}</span>
                          <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border", s.color)}>
                            {s.label}
                          </span>
                        </div>
                      </div>
                      <span className="font-semibold text-sm">{p.amount_rub?.toLocaleString('ru-RU')} ₽</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState(12);
  const [plans, setPlans] = useState(null);
  const [currentSub, setCurrentSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [consentChecked, setConsentChecked] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        subscriptionAPI.getPlans(),
        subscriptionAPI.getCurrent(),
      ]);
      setPlans(plansRes.data);
      setCurrentSub(subRes.data);
    } catch (err) {
      console.error('Failed to load plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPricing = () => {
    if (!plans?.pro?.periods) return null;
    return plans.pro.periods.find(p => p.period_months === selectedPeriod);
  };

  const pricing = getCurrentPricing();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isProActive = currentSub?.plan === 'pro';
  const basePrice = plans?.pro?.base_price || 360;
  const pricePerMonth = pricing?.price_per_month || basePrice;
  const totalPrice = pricing?.total || basePrice;
  const pricePerDay = Math.round(totalPrice / (selectedPeriod * 30));

  return (
    <div className="space-y-6 sm:space-y-8 pb-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Тарифы</h1>
        <p className="text-muted-foreground text-sm mt-1">Выберите подходящий план для вашего бизнеса</p>
      </div>

      {/* Current Plan Badge */}
      {isProActive && currentSub?.subscription && (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <Crown className="h-5 w-5 text-emerald-500" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">Тариф «Про» активен</span>
                  <Badge variant="outline" className="border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
                    {currentSub.subscription.period_months} мес.
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Действует до {new Date(currentSub.subscription.expires_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Period Selector */}
      <div className="flex justify-center">
        <div className="inline-flex items-center bg-muted rounded-xl p-1.5 gap-1.5 border border-border">
          {PERIODS.map((period) => {
            const isSelected = selectedPeriod === period.months;
            const periodPricing = plans?.pro?.periods?.find(p => p.period_months === period.months);
            return (
              <button
                key={period.months}
                onClick={() => setSelectedPeriod(period.months)}
                className={cn(
                  "relative px-4 sm:px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                  isSelected
                    ? "bg-orange-500 text-white shadow-md shadow-orange-500/25"
                    : "bg-background text-foreground hover:bg-accent border border-border/50"
                )}
              >
                <span className="relative z-10">{period.label}</span>
                {periodPricing?.discount_percent > 0 && (
                  <span className={cn(
                    "absolute -top-2.5 -right-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm",
                    isSelected ? "bg-white text-orange-600" : "bg-emerald-500 text-white"
                  )}>
                    -{periodPricing.discount_percent}%
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ══════ Plans Grid ══════ */}
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {/* ── Demo Plan ── */}
        <Card className={cn(
          "relative overflow-hidden transition-all duration-300 border-t-[3px]",
          !isProActive
            ? "ring-2 ring-primary/20 border-t-primary/40"
            : "border-t-muted-foreground/20"
        )}>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                  <Shield className="h-5 w-5 text-muted-foreground" />
                </div>
                <CardTitle className="text-lg">Демо</CardTitle>
              </div>
              {!isProActive && (
                <Badge variant="secondary" className="text-xs">Текущий</Badge>
              )}
            </div>
            <div className="mt-4">
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold">0 ₽</span>
                <span className="text-muted-foreground text-sm">/навсегда</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Попробуйте CRM бесплатно — но с жёсткими лимитами
              </p>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {/* Key limits highlighted */}
            <div className="rounded-lg border border-orange-500/20 bg-orange-500/5 p-3 mb-4">
              <div className="text-xs font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5" />
                Ограничения
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Заказы', val: '3' },
                  { label: 'Задачи', val: '3' },
                  { label: 'Клиенты', val: '3' },
                  { label: 'Материалы', val: '1' },
                  { label: 'Счета', val: '3' },
                ].map(l => (
                  <div key={l.label} className="flex items-center gap-1.5 text-sm">
                    <span className="font-bold text-orange-600 dark:text-orange-400">{l.val}</span>
                    <span className="text-muted-foreground">{l.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2.5">
              {[
                { icon: FileText, text: 'Конструктор форм', ok: true },
                { icon: BarChart3, text: 'Дашборд и аналитика', ok: true },
                { icon: Wallet, text: 'Финансы: базовый', ok: true },
                { icon: Receipt, text: 'Счета и оплаты', ok: true },
                { icon: Bell, text: 'Уведомления (Telegram + Email)', ok: true },
                { icon: CalendarDays, text: 'Календарь и отчёты', ok: true },
                { icon: Download, text: 'Экспорт данных', ok: true },
                { icon: Globe, text: 'Публичная страница бизнеса', ok: false },
                { icon: HeadphonesIcon, text: 'Приоритетная поддержка', ok: false },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  {f.ok ? (
                    <div className="h-5 w-5 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                      <Check className="h-3 w-3 text-emerald-500" />
                    </div>
                  ) : (
                    <div className="h-5 w-5 rounded-full bg-red-500/10 flex items-center justify-center shrink-0">
                      <X className="h-3 w-3 text-red-400" />
                    </div>
                  )}
                  <span className={cn("text-sm", !f.ok && "text-muted-foreground/50 line-through")}>{f.text}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* ── Pro Plan ── */}
        <Card className={cn(
          "relative overflow-hidden transition-all duration-300 border-t-[3px] border-t-emerald-500",
          isProActive
            ? "ring-2 ring-emerald-500/30 border-emerald-500/30"
            : "ring-2 ring-emerald-500/50 border-emerald-500/30 shadow-lg shadow-emerald-500/5"
        )}>
          {/* Popular Badge */}
          {!isProActive && (
            <div className="absolute top-3 right-3">
              <Badge className="bg-emerald-500 text-white hover:bg-emerald-600 gap-1">
                <Sparkles className="h-3 w-3" />
                Рекомендуем
              </Badge>
            </div>
          )}

          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Crown className="h-5 w-5 text-emerald-500" />
              </div>
              <CardTitle className="text-lg">Про</CardTitle>
              {isProActive && (
                <Badge variant="outline" className="ml-auto border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-xs">
                  Активен
                </Badge>
              )}
            </div>
            <div className="mt-4">
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold">{pricePerMonth} ₽</span>
                <span className="text-muted-foreground text-sm">/месяц</span>
              </div>
              {pricing?.discount_percent > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-muted-foreground line-through">{basePrice} ₽</span>
                  <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs">
                    Экономия {basePrice * selectedPeriod - totalPrice} ₽
                  </Badge>
                </div>
              )}
              <div className="flex items-center gap-3 mt-2">
                <div className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {totalPrice} ₽ за {selectedPeriod === 1 ? 'месяц' : `${selectedPeriod} мес.`}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground/50">|</span>
                <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                  ≈ {pricePerDay} ₽/день
                </span>
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-0">
            {/* Unlimited badge */}
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3 mb-4">
              <div className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Infinity className="h-3.5 w-3.5" />
                Без ограничений
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Заказы', val: '∞' },
                  { label: 'Задачи', val: '∞' },
                  { label: 'Клиенты', val: '∞' },
                  { label: 'Материалы', val: '∞' },
                  { label: 'Счета', val: '∞' },
                ].map(l => (
                  <div key={l.label} className="flex items-center gap-1.5 text-sm">
                    <span className="font-bold text-emerald-600 dark:text-emerald-400">{l.val}</span>
                    <span className="text-muted-foreground">{l.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2.5 mb-6">
              {[
                { icon: FileText, text: 'Конструктор форм' },
                { icon: BarChart3, text: 'Полная аналитика и дашборд' },
                { icon: Wallet, text: 'Финансы: доходы, расходы, графики' },
                { icon: Receipt, text: 'Безлимитные счета с публичными ссылками' },
                { icon: Bell, text: 'Уведомления (Telegram + Email)' },
                { icon: CalendarDays, text: 'Календарь и отчёты' },
                { icon: Download, text: 'Экспорт данных' },
                { icon: Globe, text: 'Публичная страница с портфолио' },
                { icon: HeadphonesIcon, text: 'Приоритетная поддержка' },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="h-5 w-5 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                    <Check className="h-3 w-3 text-emerald-500" />
                  </div>
                  <span className="text-sm">{f.text}</span>
                </div>
              ))}
            </div>

            {/* CTA area */}
            {!isProActive && (
              <div className="space-y-4">
                {/* Payment summary */}
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3.5 space-y-2.5">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <Receipt className="h-4 w-4 text-emerald-500" />
                    Сводка по оплате
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Тариф</span>
                      <span className="font-medium">Про — {selectedPeriod === 1 ? '1 месяц' : selectedPeriod === 3 ? '3 месяца' : selectedPeriod === 6 ? '6 месяцев' : '12 месяцев'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">К оплате</span>
                      <span className="font-semibold text-foreground">{totalPrice} ₽</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Тип оплаты</span>
                      <span>Разовый платёж</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Доступ до</span>
                      <span>{new Date(Date.now() + selectedPeriod * 30 * 24 * 60 * 60 * 1000).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    </div>
                  </div>
                </div>

                {/* Consent checkbox */}
                <label
                  htmlFor="subscription-consent"
                  className="flex items-start gap-3 cursor-pointer select-none group"
                >
                  <input
                    id="subscription-consent"
                    type="checkbox"
                    checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    className="mt-0.5 h-[18px] w-[18px] min-w-[18px] rounded border-2 border-border accent-emerald-500 cursor-pointer transition-colors focus:ring-2 focus:ring-emerald-500/30 focus:ring-offset-0"
                  />
                  <span className="text-[13px] leading-[1.5] text-foreground/80 group-hover:text-foreground transition-colors">
                    Я соглашаюсь с условиями{' '}
                    <Link
                      to="/subscription-terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-600 dark:text-emerald-400 underline underline-offset-2 hover:text-emerald-700 dark:hover:text-emerald-300 font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      оферты подписки
                    </Link>{' '}
                    и даю согласие на обработку персональных данных в соответствии с{' '}
                    <Link
                      to="/personal-data-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-600 dark:text-emerald-400 underline underline-offset-2 hover:text-emerald-700 dark:hover:text-emerald-300 font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Политикой обработки персональных данных
                    </Link>
                  </span>
                </label>

                <Button
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-11 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!consentChecked || paymentLoading}
                  onClick={async () => {
                    setPaymentLoading(true);
                    try {
                      const res = await paymentAPI.init({ period_months: selectedPeriod });
                      if (res.data.payment_url) {
                        window.location.href = res.data.payment_url;
                      } else {
                        toast.error('Не удалось получить ссылку на оплату');
                      }
                    } catch (err) {
                      const msg = err.response?.data?.detail || 'Ошибка при создании платежа';
                      toast.error(msg);
                    } finally {
                      setPaymentLoading(false);
                    }
                  }}
                >
                  {paymentLoading ? (
                    <>
                      <span className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                      Переход к оплате...
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4 mr-2" />
                      Перейти на Про — {totalPrice} ₽
                    </>
                  )}
                </Button>
                <p className="text-center text-xs text-muted-foreground">
                  14 дней на возврат · Безопасная оплата через Т-Банк
                </p>
              </div>
            )}

            {/* Renew/Extend area for Pro users */}
            {isProActive && currentSub?.subscription && (
              <div className="space-y-4">
                {/* Current subscription info */}
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3.5 space-y-2.5">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <Crown className="h-4 w-4 text-emerald-500" />
                    Ваша подписка
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Текущий период</span>
                      <span className="font-medium">{currentSub.subscription.period_months} мес.</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Действует до</span>
                      <span className="font-semibold">{new Date(currentSub.subscription.expires_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    </div>
                    {(() => {
                      const daysLeft = Math.ceil((new Date(currentSub.subscription.expires_at) - new Date()) / (1000 * 60 * 60 * 24));
                      return (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Осталось</span>
                          <span className={cn("font-medium", daysLeft <= 7 ? "text-red-500" : daysLeft <= 30 ? "text-amber-500" : "text-emerald-600 dark:text-emerald-400")}>
                            {daysLeft > 0 ? `${daysLeft} ${daysLeft === 1 ? 'день' : daysLeft < 5 ? 'дня' : 'дней'}` : 'Истекает сегодня'}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Extend payment summary */}
                <div className="rounded-lg border border-border bg-muted/30 p-3.5 space-y-2.5">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <TrendingUp className="h-4 w-4 text-emerald-500" />
                    Продлить подписку
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Добавить</span>
                      <span className="font-medium">{selectedPeriod === 1 ? '1 месяц' : selectedPeriod === 3 ? '3 месяца' : selectedPeriod === 6 ? '6 месяцев' : '12 месяцев'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">К оплате</span>
                      <span className="font-semibold text-foreground">{totalPrice} ₽</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Новая дата окончания</span>
                      <span className="font-medium text-emerald-600 dark:text-emerald-400">
                        {new Date(new Date(currentSub.subscription.expires_at).getTime() + selectedPeriod * 30 * 24 * 60 * 60 * 1000).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Новый период добавится к текущей подписке
                  </p>
                </div>

                {/* Consent checkbox */}
                <label
                  htmlFor="subscription-consent-renew"
                  className="flex items-start gap-3 cursor-pointer select-none group"
                >
                  <input
                    id="subscription-consent-renew"
                    type="checkbox"
                    checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    className="mt-0.5 h-[18px] w-[18px] min-w-[18px] rounded border-2 border-border accent-emerald-500 cursor-pointer transition-colors focus:ring-2 focus:ring-emerald-500/30 focus:ring-offset-0"
                  />
                  <span className="text-[13px] leading-[1.5] text-foreground/80 group-hover:text-foreground transition-colors">
                    Я соглашаюсь с условиями{' '}
                    <Link
                      to="/subscription-terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-600 dark:text-emerald-400 underline underline-offset-2 hover:text-emerald-700 dark:hover:text-emerald-300 font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      оферты подписки
                    </Link>
                  </span>
                </label>

                <Button
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-11 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!consentChecked || paymentLoading}
                  onClick={async () => {
                    setPaymentLoading(true);
                    try {
                      const res = await paymentAPI.init({ period_months: selectedPeriod });
                      if (res.data.payment_url) {
                        window.location.href = res.data.payment_url;
                      } else {
                        toast.error('Не удалось получить ссылку на оплату');
                      }
                    } catch (err) {
                      const msg = err.response?.data?.detail || 'Ошибка при создании платежа';
                      toast.error(msg);
                    } finally {
                      setPaymentLoading(false);
                    }
                  }}
                >
                  {paymentLoading ? (
                    <>
                      <span className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                      Переход к оплате...
                    </>
                  ) : (
                    <>
                      <ArrowRight className="h-4 w-4 mr-2" />
                      Продлить на {selectedPeriod === 1 ? '1 месяц' : selectedPeriod === 3 ? '3 месяца' : selectedPeriod === 6 ? '6 месяцев' : '12 месяцев'} — {totalPrice} ₽
                    </>
                  )}
                </Button>
                <p className="text-center text-xs text-muted-foreground">
                  Разовый платёж · Безопасная оплата через Т-Банк
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>


      {/* comparison table removed for simplicity */}


      {/* Usage Stats for Demo users */}
      {!isProActive && currentSub?.usage && (
        <Card className="max-w-4xl mx-auto mt-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              Ваше текущее использование
            </CardTitle>
            <p className="text-xs text-muted-foreground">Лимиты, которые исчезнут с Про</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {[
                { label: 'Заказы', current: currentSub.usage.orders, limit: currentSub.limits.orders, icon: ShoppingCart },
                { label: 'Задачи', current: currentSub.usage.tasks, limit: currentSub.limits.tasks, icon: CheckSquare },
                { label: 'Клиенты', current: currentSub.usage.customers, limit: currentSub.limits.customers, icon: Users },
                { label: 'Материалы', current: currentSub.usage.materials || 0, limit: currentSub.limits.materials || 1, icon: Package },
                { label: 'Счета', current: currentSub.usage.invoices || 0, limit: currentSub.limits.invoices || 3, icon: Receipt },
              ].map((item) => {
                const percent = item.limit > 0 ? Math.min((item.current / item.limit) * 100, 100) : 0;
                const isAtLimit = item.current >= item.limit;
                return (
                  <div key={item.label} className="text-center">
                    <div className={cn(
                      "h-10 w-10 rounded-lg mx-auto flex items-center justify-center mb-2",
                      isAtLimit ? "bg-red-500/10" : "bg-muted"
                    )}>
                      <item.icon className={cn("h-5 w-5", isAtLimit ? "text-red-500" : "text-muted-foreground")} />
                    </div>
                    <div className={cn("text-2xl font-bold", isAtLimit && "text-red-500")}>
                      {item.current}/{item.limit}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{item.label}</div>
                    <div className="w-full bg-muted rounded-full h-1.5 mt-2">
                      <div
                        className={cn(
                          "h-1.5 rounded-full transition-all",
                          isAtLimit ? "bg-red-500" : percent > 60 ? "bg-amber-500" : "bg-emerald-500"
                        )}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    {isAtLimit && (
                      <span className="text-[10px] text-red-500 font-medium mt-1 block">Лимит достигнут</span>
                    )}
                  </div>
                );
              })}
            </div>
            {/* CTA after usage */}
            <div className="mt-5 pt-4 border-t text-center">
              <p className="text-sm text-muted-foreground mb-2">
                С тарифом <span className="font-semibold text-emerald-600 dark:text-emerald-400">Про</span> эти лимиты исчезнут навсегда
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment History */}
      <PaymentHistorySection />

      {/* FAQ */}
      <div className="max-w-4xl mx-auto">
        <h2 className="text-lg font-semibold mb-4 text-center">Частые вопросы</h2>
        <Accordion type="multiple" className="grid sm:grid-cols-2 gap-x-4 gap-y-0">
          {[
            { q: 'Могу ли я попробовать бесплатно?', a: 'Да! Демо-версия доступна бесплатно без ограничения по времени. Вы можете создать до 3 заказов, 3 задач, 3 клиентов, 3 счетов и 1 материал.' },
            { q: 'Как происходит оплата?', a: 'Оплата производится банковской картой (Visa, Mastercard, МИР) через безопасный платёжный сервис Т-Банк. Вы оплачиваете выбранный период единоразово. По окончании периода подписка не продлевается автоматически — вы сами решаете, продлить её или нет.' },
            { q: 'Подписка продлевается автоматически?', a: 'Нет. Автоматического списания нет. По окончании оплаченного периода подписка завершается, и аккаунт переходит на демо-версию. Чтобы продолжить пользоваться всеми функциями, нужно оплатить новый период.' },
            { q: 'Как продлить подписку?', a: 'За день до окончания вы получите уведомление. Чтобы продлить, перейдите на страницу тарифов и оплатите новый период. Если у вас есть активная подписка, новый период добавится к текущему.' },
            { q: 'Можно ли вернуть деньги?', a: 'Да. В течение 14 дней с момента оплаты — полный возврат, если платными функциями не пользовались. При технических сбоях более 5 дней — пропорциональный возврат. Подробнее — в оферте подписки.' },
            { q: 'Можно ли сменить период?', a: 'Да, при продлении подписки вы можете выбрать любой период. Чем длиннее период, тем больше скидка — до 28% при оплате за год.' },
            { q: 'Что будет с данными после окончания подписки?', a: 'Ваши данные сохранятся. Вы вернётесь на демо-версию с ограничениями на создание новых записей, но все существующие данные останутся доступны.' },
            { q: 'Безопасно ли оплачивать картой?', a: 'Да. Все платежи защищены технологией 3-D Secure и обрабатываются АО «Тинькофф Банк». Мы не храним полные данные вашей карты — они обрабатываются платёжным провайдером по стандарту PCI DSS.' },
          ].map((item, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-b-0 mb-2">
              <Card className="shadow">
                <AccordionTrigger className="px-4 py-3 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                  <span className="font-medium text-sm text-left">{item.q}</span>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-3 pt-0">
                  <p className="text-sm text-muted-foreground">{item.a}</p>
                </AccordionContent>
              </Card>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
}
